import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { RSVPResponse } from '../../../types/invitation';
import { seatingService, rsvpService } from '../../../lib/supabase';
import { collaborationService } from '../../../lib/collaborationService';
import { createLogger } from '../../../lib/logger';
import { Table, Guest, SeatingStats } from '../types';

const log = createLogger('seating');

const DEFAULT_TABLES: Table[] = [];
const CACHE_KEY = 'seatingArrangements';

const extractTableNumber = (tableName: string): number => {
  const matches = tableName.match(/(\d+)/);
  return matches ? parseInt(matches[1], 10) : Infinity;
};

const sortTablesByNumber = (tables: Table[]): Table[] => {
  return [...tables].sort((a, b) => {
    const numA = extractTableNumber(a.name);
    const numB = extractTableNumber(b.name);
    if (numA !== numB) return numA - numB;
    return a.id - b.id;
  });
};

interface UseSeatingDataOptions {
  inviteCode: string;
  userId?: string;
  confirmedAttendees: RSVPResponse[];
  refreshTrigger?: number;
  showCollaborationAlert: (editor: string, action: string) => void;
}

export function useSeatingData({
  inviteCode,
  userId,
  confirmedAttendees,
  refreshTrigger,
  showCollaborationAlert,
}: UseSeatingDataOptions) {
  const [tables, setTables] = useState<Table[]>(DEFAULT_TABLES);
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(false);
  const [defaultCapacity, setDefaultCapacity] = useState(10);
  const [localConfirmedAttendees, setLocalConfirmedAttendees] = useState<RSVPResponse[]>(confirmedAttendees);

  const [syncBlocked, setSyncBlocked] = useState(false);
  const [syncReady, setSyncReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<Date | null>(null);

  const saveChainRef = useRef<Promise<void>>(Promise.resolve());

  const readCache = useCallback((): Table[] | null => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;
      const all = JSON.parse(raw);
      return all[inviteCode] ?? null;
    } catch {
      return null;
    }
  }, [inviteCode]);

  const writeCache = useCallback((data: Table[]) => {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      const all = raw ? JSON.parse(raw) : {};
      all[inviteCode] = data;
      localStorage.setItem(CACHE_KEY, JSON.stringify(all));
    } catch {
      // cache write failure doesn't affect main flow
    }
  }, [inviteCode]);

  const loadSeatingArrangement = useCallback(async () => {
    const cached = readCache();
    if (cached) {
      setTables(cached);
    }

    try {
      const supabaseData = await seatingService.loadSeatingArrangement(inviteCode);

      if (supabaseData) {
        setTables(supabaseData);
        writeCache(supabaseData);
      } else {
        setTables([]);
        writeCache([]);
      }

      setSyncReady(true);
      setLoadError(null);
      return true;
    } catch (error) {
      const message = error instanceof Error ? error.message : '未知錯誤';
      log.error('Failed to load seating arrangement:', error);
      setSyncReady(false);
      setLoadError(message);
      return false;
    }
  }, [inviteCode, readCache, writeCache]);

  const saveSeatingArrangement = useCallback((newTables: Table[]) => {
    const doSave = async () => {
      try {
        if (userId) {
          const check = await collaborationService.checkRecentActivity(
            'seating_arrangements',
            inviteCode,
            userId
          );
          if (check.hasConflict) {
            showCollaborationAlert(check.lastEditor || '其他協作者', '修改了座位安排');
          }
        }

        await seatingService.saveSeatingArrangement(inviteCode, newTables);

        if (userId) {
          await collaborationService.updateCollaborationMetadata(
            'seating_arrangements',
            inviteCode,
            userId,
            inviteCode
          );
        }

        writeCache(newTables);
        setLastSyncedAt(new Date());
        setSyncBlocked(false);
        setSyncError(null);
      } catch (error) {
        const message = error instanceof Error ? error.message : '未知錯誤';
        log.error('Failed to save to Supabase:', error);
        setSyncBlocked(true);
        setSyncError(message);
      }
    };
    saveChainRef.current = saveChainRef.current.then(doSave, doSave);
  }, [inviteCode, userId, showCollaborationAlert, writeCache]);

  const handleRefresh = useCallback(async () => {
    if (!userId || syncBlocked || !syncReady) return;

    try {
      setLoading(true);
      await saveChainRef.current;

      const attendees = await rsvpService.getRSVPsByInviteCode(inviteCode, userId);
      const confirmed = attendees.filter(a => a.rsvpData.attending === 'yes');
      setLocalConfirmedAttendees(confirmed);

      await loadSeatingArrangement();
    } catch (error) {
      log.error('Refresh failed:', error);
    } finally {
      setLoading(false);
    }
  }, [userId, inviteCode, loadSeatingArrangement, syncBlocked, syncReady]);

  const retrySync = useCallback(async () => {
    setLoading(true);
    saveSeatingArrangement(tables);
    try {
      await saveChainRef.current;
    } finally {
      setLoading(false);
    }
  }, [tables, saveSeatingArrangement]);

  const retryLoad = useCallback(async () => {
    try {
      setLoading(true);
      await loadSeatingArrangement();
    } finally {
      setLoading(false);
    }
  }, [loadSeatingArrangement]);

  const addNewTable = useCallback(() => {
    if (syncBlocked || !syncReady) return;

    const newId = tables.length > 0 ? Math.max(...tables.map(t => t.id)) + 1 : 1;
    const newTable: Table = {
      id: newId,
      name: `Table ${newId}`,
      capacity: defaultCapacity,
      guests: [],
    };

    const sortedTables = sortTablesByNumber([...tables, newTable]);
    setTables(sortedTables);
    saveSeatingArrangement(sortedTables);
  }, [tables, defaultCapacity, saveSeatingArrangement, syncBlocked, syncReady]);

  const deleteTable = useCallback((tableId: number) => {
    if (syncBlocked || !syncReady) return;

    const newTables = tables.filter(t => t.id !== tableId);
    setTables(newTables);
    saveSeatingArrangement(newTables);
  }, [tables, saveSeatingArrangement, syncBlocked, syncReady]);

  const updateTableName = useCallback((tableId: number, newName: string) => {
    if (syncBlocked || !syncReady) return;

    const updatedTables = tables.map(table =>
      table.id === tableId ? { ...table, name: newName } : table
    );
    const sortedTables = sortTablesByNumber(updatedTables);
    setTables(sortedTables);
    saveSeatingArrangement(sortedTables);
  }, [tables, saveSeatingArrangement, syncBlocked, syncReady]);

  const removeGuestFromTable = useCallback((tableId: number, guestId: string) => {
    if (syncBlocked || !syncReady) return;

    const updatedTables = tables.map(table => {
      if (table.id === tableId) {
        return { ...table, guests: table.guests.filter(g => g.id !== guestId) };
      }
      return table;
    });
    setTables(updatedTables);
    saveSeatingArrangement(updatedTables);
  }, [tables, saveSeatingArrangement, syncBlocked, syncReady]);

  const getLatestGuestCount = useCallback((seatedGuest: Guest): number => {
    const latestRSVP = localConfirmedAttendees.find(rsvp => rsvp.id === seatedGuest.id);
    return latestRSVP ? latestRSVP.rsvpData.guestCount : (seatedGuest.guestCount || 1);
  }, [localConfirmedAttendees]);

  const getLatestGuestGroup = useCallback((seatedGuest: Guest): string => {
    const latestCount = getLatestGuestCount(seatedGuest);
    return `確認出席 (${latestCount}人)`;
  }, [getLatestGuestCount]);

  const distributeDietaryRequirements = useCallback((guest: Guest, guestCount: number): string[] => {
    const latestRSVP = localConfirmedAttendees.find(rsvp => rsvp.id === guest.id);
    const dietaryRequirements = latestRSVP?.rsvpData.dietaryRestrictions || [];

    const requirementPool: string[] = [];
    dietaryRequirements.forEach(req => {
      const label = req.id === 'vegetarian' ? '素食' : '兒童椅';
      for (let i = 0; i < req.count; i++) {
        requirementPool.push(label);
      }
    });

    const distributions: string[] = [];

    if (requirementPool.length === 0) {
      for (let i = 0; i < guestCount; i++) {
        distributions.push('');
      }
    } else {
      const firstPersonRequirements: string[] = [];
      const uniqueTypes = [...new Set(requirementPool)];

      uniqueTypes.forEach(type => {
        firstPersonRequirements.push(type);
        const index = requirementPool.indexOf(type);
        if (index > -1) {
          requirementPool.splice(index, 1);
        }
      });

      distributions.push(firstPersonRequirements.join('＆'));

      const remainingCounts: { [key: string]: number } = {};
      requirementPool.forEach(req => {
        remainingCounts[req] = (remainingCounts[req] || 0) + 1;
      });

      const sortedRequirements = Object.entries(remainingCounts)
        .sort((a, b) => b[1] - a[1])
        .flatMap(([type, count]) => Array(count).fill(type));

      let reqIndex = 0;
      for (let i = 1; i < guestCount; i++) {
        if (reqIndex < sortedRequirements.length) {
          distributions.push(sortedRequirements[reqIndex]);
          reqIndex++;
        } else {
          distributions.push('');
        }
      }
    }

    return distributions;
  }, [localConfirmedAttendees]);

  const exportSeatingArrangement = useCallback(() => {
    const csvData: string[][] = [];

    csvData.push(['桌號', '賓客姓名', '分組', '電子郵件', '聯絡電話', '特殊需求']);

    tables.forEach(table => {
      table.guests.forEach(guest => {
        const latestGuestCount = getLatestGuestCount(guest);
        const distributions = distributeDietaryRequirements(guest, latestGuestCount);

        for (let i = 0; i < latestGuestCount; i++) {
          if (i === 0) {
            csvData.push([
              table.name,
              guest.name,
              getLatestGuestGroup(guest),
              guest.email || '',
              guest.phone || '',
              distributions[i] || '',
            ]);
          } else {
            csvData.push([
              table.name,
              guest.name,
              `${getLatestGuestGroup(guest).replace(/\(\d+人\)/, '')}(${latestGuestCount}人同行)`,
              '',
              '',
              distributions[i] || '',
            ]);
          }
        }
      });

      const usedSeats = table.guests.reduce((sum, guest) => sum + getLatestGuestCount(guest), 0);
      const emptySeats = table.capacity - usedSeats;

      for (let i = 0; i < emptySeats; i++) {
        csvData.push([table.name, '', '', '', '', '']);
      }
    });

    const currentFilteredGuests = guests.filter(
      guest => !tables.some(table => table.guests.some(g => g.id === guest.id))
    );

    currentFilteredGuests.forEach(guest => {
      const guestCountVal = guest.guestCount || 1;
      const distributions = distributeDietaryRequirements(guest, guestCountVal);

      for (let i = 0; i < guestCountVal; i++) {
        if (i === 0) {
          csvData.push([
            '未安排',
            guest.name,
            guest.group || '',
            guest.email || '',
            guest.phone || '',
            distributions[i] || '',
          ]);
        } else {
          csvData.push([
            '未安排',
            guest.name,
            `${guest.group?.replace(/\(\d+人\)/, '') || ''}(${guestCountVal}人同行)`,
            '',
            '',
            distributions[i] || '',
          ]);
        }
      }
    });

    const totalActualGuests = guests.reduce((sum, guest) => sum + (guest.guestCount || 1), 0);
    const assignedActualGuests = tables.reduce(
      (sum, table) => sum + table.guests.reduce((tableSum, guest) => tableSum + (guest.guestCount || 1), 0),
      0
    );
    const unassignedActualGuests = currentFilteredGuests.reduce(
      (sum, guest) => sum + (guest.guestCount || 1),
      0
    );

    csvData.push(['', '', '', '', '', '']);
    csvData.push(['=== 統計資訊 ===', '', '', '', '', '']);
    csvData.push(['總桌數', tables.length.toString(), '', '', '', '']);
    csvData.push(['總賓客組數', guests.length.toString(), '', '', '', '']);
    csvData.push(['總出席人數', totalActualGuests.toString(), '', '', '', '']);
    csvData.push(['已安排座位人數', assignedActualGuests.toString(), '', '', '', '']);
    csvData.push(['未安排座位人數', unassignedActualGuests.toString(), '', '', '', '']);

    csvData.push(['', '', '', '', '', '']);
    csvData.push(['=== 各桌人數 ===', '', '', '', '']);
    tables.forEach(table => {
      const actualGuestsInTable = table.guests.reduce(
        (sum, guest) => sum + (guest.guestCount || 1),
        0
      );
      csvData.push([table.name, `${actualGuestsInTable}/${table.capacity}`, '', '', '', '']);
    });

    const csvContent = csvData
      .map(row => row.map(cell => `"${cell.replace(/"/g, '""')}"`).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `座位安排-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();

    URL.revokeObjectURL(link.href);
  }, [tables, guests, getLatestGuestCount, getLatestGuestGroup, distributeDietaryRequirements]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      await loadSeatingArrangement();
    };
    loadData();
  }, [inviteCode]);

  // Sync external confirmedAttendees
  useEffect(() => {
    log.debug(`External confirmedAttendees updated, ${confirmedAttendees.length} guests`);
    setLocalConfirmedAttendees(confirmedAttendees);
  }, [confirmedAttendees]);

  // Sync seated guest counts with latest RSVP data
  useEffect(() => {
    const syncSeatedGuestCounts = () => {
      log.debug('Syncing seated guest counts');

      let hasUpdates = false;
      const updatedTables = tables.map(table => {
        const updatedGuests = table.guests.map(seatedGuest => {
          const latestRSVP = localConfirmedAttendees.find(rsvp => rsvp.id === seatedGuest.id);

          if (latestRSVP && latestRSVP.rsvpData.guestCount !== seatedGuest.guestCount) {
            log.debug(
              `Updating guest count: ${seatedGuest.name} ${seatedGuest.guestCount} -> ${latestRSVP.rsvpData.guestCount}`
            );
            hasUpdates = true;
            return {
              ...seatedGuest,
              guestCount: latestRSVP.rsvpData.guestCount,
              group: `確認出席 (${latestRSVP.rsvpData.guestCount}人)`,
            };
          }

          return seatedGuest;
        });

        return { ...table, guests: updatedGuests };
      });

      if (hasUpdates) {
        log.info('Seated guest counts synced, forcing state update');
        setTables([...updatedTables]);
        if (!syncBlocked && syncReady) {
          saveSeatingArrangement(updatedTables);
        }
      }
    };

    if (tables.length > 0 && localConfirmedAttendees.length > 0) {
      syncSeatedGuestCounts();
    }
  }, [localConfirmedAttendees]);

  // Convert RSVP confirmed attendees to Guest format
  useEffect(() => {
    const convertRSVPToGuests = () => {
      log.debug(`Converting RSVP to guest list, refreshTrigger=${refreshTrigger}`);
      log.debug(`Received ${localConfirmedAttendees.length} confirmed attendees`);

      const convertedGuests: Guest[] = [];

      localConfirmedAttendees.forEach((rsvp) => {
        if (rsvp.rsvpData.attending === 'yes') {
          const guest: Guest = {
            id: rsvp.id,
            name: rsvp.guestName,
            group: `確認出席 (${rsvp.rsvpData.guestCount}人)`,
            email: rsvp.guestEmail,
            phone: rsvp.rsvpData.contactPhone,
            guestCount: rsvp.rsvpData.guestCount,
            dietaryRestrictions:
              rsvp.rsvpData.dietaryRestrictions
                ?.map(dr => `${dr.id === 'vegetarian' ? '素食' : '兒童椅'} ${dr.count}`)
                .join(', ') || undefined,
          };

          const isAssigned = tables.some(table =>
            table.guests.some(assignedGuest => assignedGuest.id === guest.id)
          );

          if (!isAssigned) {
            convertedGuests.push(guest);
          }
        }
      });

      log.debug(
        `Guest loading complete: ${localConfirmedAttendees.length} guests, ${convertedGuests.length} unassigned`
      );
      setGuests(convertedGuests);
    };

    convertRSVPToGuests();
  }, [localConfirmedAttendees, tables, refreshTrigger]);

  const stats: SeatingStats = useMemo(() => {
    const totalGuests = localConfirmedAttendees.reduce(
      (sum, rsvp) =>
        rsvp.rsvpData.attending === 'yes' ? sum + (rsvp.rsvpData.guestCount || 1) : sum,
      0
    );

    const assignedGuests = tables.reduce((sum, table) => {
      return (
        sum +
        table.guests.reduce((tableSum, seatedGuest) => {
          const latestRSVP = localConfirmedAttendees.find(rsvp => rsvp.id === seatedGuest.id);
          const actualGuestCount = latestRSVP
            ? latestRSVP.rsvpData.guestCount
            : seatedGuest.guestCount || 1;
          return tableSum + actualGuestCount;
        }, 0)
      );
    }, 0);

    const unassignedGuests = Math.max(0, totalGuests - assignedGuests);
    const totalCapacity = tables.reduce((sum, table) => sum + table.capacity, 0);

    return { totalGuests, assignedGuests, unassignedGuests, totalCapacity };
  }, [tables, localConfirmedAttendees]);

  return {
    tables,
    setTables,
    guests,
    loading,
    lastSyncedAt,
    defaultCapacity,
    setDefaultCapacity,
    stats,
    loadSeatingArrangement,
    saveSeatingArrangement,
    handleRefresh,
    addNewTable,
    deleteTable,
    updateTableName,
    removeGuestFromTable,
    getLatestGuestCount,
    getLatestGuestGroup,
    exportSeatingArrangement,
    syncBlocked,
    syncReady,
    loadError,
    syncError,
    retrySync,
    retryLoad,
  };
}
